// Banner de última oportunidad con información y reglas para ajustar ofertas

import React from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import CountdownTimer from '../CountdownTimer';
import { useTranslation, Trans } from 'react-i18next';
import { formatNumber } from '../../utils/formatCurrency';

/**
 * Banner que se muestra durante el periodo de última oportunidad
 * Solo visible para usuarios que:
 * - Ya pujaron antes (tienen userLastBid)
 * - No son el ganador actual (!isTopBidder)
 * - No han usado su oportunidad de ajuste (canBidInLastChance)
 *
 * @param {boolean} isVisible - Control de visibilidad (calculado externamente)
 * @param {string} timeRemaining - Tiempo restante formateado (ej: "48h", "1d 12h")
 * @param {object} userLastBid - Última oferta del usuario { amount, timestamp, ... }
 * @param {object} highestBid - Oferta más alta actual { amount, ... }
 * @param {number} currentPrice - Precio actual (startPrice o highestBid.amount)
 * @param {object} lastChanceBidRange - Rango válido { min, max }
 */
export const LastChanceBanner = ({
  isVisible,
  lastChanceEnd,
  userLastBid,
  highestBid,
  currentPrice,
  lastChanceBidRange,
}) => {
  const { t, i18n } = useTranslation();

  // Early return si no debe mostrarse
  if (!isVisible || !userLastBid || !lastChanceBidRange) {
    return null;
  }

  const maxBid = highestBid?.amount || currentPrice;

  return (
    <div className="mb-6 bg-gradient-to-r from-amber-500/10 via-amber-400/10 to-orange-500/10 border-2 border-amber-500/30 rounded-2xl p-4 md:p-6 backdrop-blur-sm animate-pulse-slow">
      <div className="flex items-start gap-4">
        {/* Icono */}
        <div className="flex-shrink-0">
          <div className="bg-amber-500/20 p-3 rounded-xl">
            <Clock className="w-6 h-6 md:w-8 md:h-8 text-amber-400" />
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl font-bold text-amber-400 mb-2 flex items-center gap-2">
            {t('lastChance.title')}
          </h3>

          <p className="text-sm md:text-base text-slate-200 mb-4">
            <Trans
              i18nKey="lastChance.intro"
              components={{ timer: <CountdownTimer deadline={lastChanceEnd} className="font-bold text-amber-400" /> }}
            />
          </p>

          {/* Mensaje destacado */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-3">
            <div className="text-sm md:text-base text-slate-300 space-y-2 mb-3">
              <p><strong className="text-amber-400">{t('lastChance.youLost')}</strong> {t('lastChance.fallback')}</p>
              <p>{t('lastChance.reposition')}</p>
              <p>{t('lastChance.notMandatory')}</p>
            </div>
            <p className="text-sm md:text-base text-amber-400 italic font-semibold">
              {t('lastChance.noAction')}
            </p>
          </div>

          {/* Reglas */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs md:text-sm text-amber-200">
                <strong>{t('lastChance.rulesTitle')}</strong>
              </div>
            </div>
            <ul className="text-xs md:text-sm text-slate-300 space-y-1 ml-6 list-disc">
              <li>
                <Trans i18nKey="lastChance.rule1" components={{ strong: <strong /> }} />
              </li>
              <li>
                <Trans
                  i18nKey="lastChance.rule2"
                  values={{ min: formatNumber(lastChanceBidRange.min, i18n.language), max: formatNumber(lastChanceBidRange.max, i18n.language) }}
                  components={{ strong: <strong /> }}
                />
              </li>
              <li>{t('lastChance.rule3')}</li>
              <li>{t('lastChance.rule4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LastChanceBanner;
